/*
 OK FBI — Case Ranking Podium (Top 3)
 Cinematic FBI award ceremony podium. Physical gold / silver / bronze pedestals,
 subtle spotlight, no confetti. Reveals staggered 3rd → 2nd → 1st.
*/
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Award, ChevronRight } from "lucide-react";
import { useLeaderboard, LeaderboardPanel } from "@/components/Leaderboard";
import { sfx } from "@/lib/sounds";

const MEDAL_STYLES = {
  1: { label: "المركز الأول", medal: "GOLD", h: 220, ring: "ring-gold", fill: "linear-gradient(180deg,#C5A059 0%, #8E7132 90%)", chip: "text-black bg-gold", accent: "shadow-[0_0_60px_rgba(197,160,89,0.35)]" },
  2: { label: "المركز الثاني", medal: "SILVER", h: 165, ring: "ring-zinc-400", fill: "linear-gradient(180deg,#B7BAC0 0%, #575A62 90%)", chip: "text-black bg-zinc-300", accent: "shadow-[0_0_50px_rgba(185,190,200,0.28)]" },
  3: { label: "المركز الثالث", medal: "BRONZE", h: 130, ring: "ring-amber-800", fill: "linear-gradient(180deg,#A26A3F 0%, #5E3A1F 90%)", chip: "text-black bg-amber-700", accent: "shadow-[0_0_40px_rgba(162,106,63,0.28)]" },
};

export function CaseRankingPodium({ open, onClose }) {
  const { rows } = useLeaderboard(4000);
  const [reveal, setReveal] = useState(0); // 0=none, 1=3rd, 2=2nd, 3=1st
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    if (!open) { setReveal(0); return; }
    // Staggered reveal
    const t1 = setTimeout(() => { setReveal(1); sfx.notification(); }, 900);
    const t2 = setTimeout(() => { setReveal(2); sfx.notification(); }, 2600);
    const t3 = setTimeout(() => { setReveal(3); sfx.caseSolved(); }, 4300);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [open]);

  if (!open) return null;

  const top3 = rows.slice(0, 3);
  const order = [top3[1], top3[0], top3[2]]; // visual order: 2nd, 1st, 3rd
  const rankFor = [2, 1, 3];

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        dir="rtl" data-testid="podium-modal"
        className="fixed inset-0 z-[999] bg-gradient-to-b from-[#050508] via-black to-[#050508] text-white flex flex-col overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 shrink-0">
          <div className="flex items-center gap-3">
            <Trophy size={26} className="text-gold" strokeWidth={1.2} />
            <div>
              <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">FBI INVESTIGATORS AWARD</div>
              <div className="font-cairo text-2xl">مراسم تكريم المحققين</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowFull(true)} data-testid="podium-full-ranking"
              className="btn-ghost text-xs flex items-center gap-1">عرض الترتيب الكامل <ChevronRight size={13} /></button>
            <button onClick={onClose} data-testid="podium-close" className="btn-primary text-xs">إغلاق</button>
          </div>
        </div>

        {/* Podium stage */}
        <div className="flex-1 flex flex-col items-center justify-center relative">
          {/* subtle spotlight */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[720px] h-[520px] pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(197,160,89,0.10) 0%, rgba(0,0,0,0) 60%)" }} />

          {top3.length === 0 ? (
            <div className="text-center text-zinc-500 py-20">
              <Award size={40} className="mx-auto opacity-30" />
              <div className="mt-3">لا يوجد محققون متأهلون بعد.</div>
              <div className="text-[11px] mt-1">أضف مشاركين وقم بحل قضية أولاً.</div>
            </div>
          ) : (
            <>
              {/* Case Ranking header */}
              <motion.div initial={{ y: -18, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7 }}
                className="text-center mb-8">
                <div className="text-[10px] tracking-[0.5em] text-gold font-mono-ar mb-2">SEASON RANKING</div>
                <h2 className="font-cairo text-4xl md:text-5xl font-black text-white">أفضل ثلاثة محققين</h2>
                <div className="text-xs text-zinc-500 mt-2 tracking-[0.3em] font-mono-ar">
                  OK FBI · INVESTIGATION GAME · CEREMONY
                </div>
              </motion.div>

              <div className="flex items-end justify-center gap-4 md:gap-8 mt-4" data-testid="podium-stage">
                {[0, 1, 2].map(pos => {
                  const rank = rankFor[pos];
                  const entry = order[pos];
                  const style = MEDAL_STYLES[rank];
                  const revealed = reveal >= (4 - rank); // 3rd first, then 2nd, then 1st
                  return (
                    <div key={pos} className="flex flex-col items-center" data-testid={`podium-rank-${rank}`}>
                      {/* Investigator card */}
                      <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={revealed ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        className={`mb-3 relative`}>
                        {entry && (
                          <div className={`w-40 md:w-48 p-4 border-2 border-metal bg-black/70 backdrop-blur ${style.accent} ${rank === 1 ? "border-gold" : rank === 2 ? "border-zinc-400" : "border-amber-800"}`}>
                            {/* Medal */}
                            <div className="flex justify-center mb-2">
                              <div className={`w-14 h-14 rounded-full ${style.chip} flex items-center justify-center font-mono-ar font-black text-lg border-4 ${rank === 1 ? "border-gold" : rank === 2 ? "border-zinc-300" : "border-amber-700"}`}
                                style={{ boxShadow: "inset 0 4px 8px rgba(255,255,255,0.25), inset 0 -6px 12px rgba(0,0,0,0.4)" }}>
                                #{rank}
                              </div>
                            </div>
                            <div className="text-[8px] tracking-[0.3em] text-center text-zinc-500 font-mono-ar mb-1">{style.medal}</div>
                            <div className="text-center font-cairo text-lg text-white truncate">{entry.name}</div>
                            <div className="mt-2 border-t border-metal/40 pt-2 flex justify-between text-[10px]">
                              <span className="text-zinc-500">قضايا</span>
                              <span className="text-white font-mono-ar">{entry.correct_cases?.length || 0}</span>
                            </div>
                            <div className="flex justify-between text-[10px]">
                              <span className="text-zinc-500">النقاط</span>
                              <span className="text-gold font-mono-ar text-base tabular-nums">{entry.total_score}</span>
                            </div>
                          </div>
                        )}
                      </motion.div>
                      {/* Podium pedestal */}
                      <motion.div
                        initial={{ height: 0 }}
                        animate={revealed ? { height: style.h } : { height: 0 }}
                        transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
                        className={`w-40 md:w-52 border ${rank === 1 ? "border-gold" : rank === 2 ? "border-zinc-500" : "border-amber-800"} relative overflow-hidden`}
                        style={{ background: style.fill, boxShadow: "inset 0 4px 10px rgba(255,255,255,0.25), inset 0 -14px 30px rgba(0,0,0,0.55)" }}>
                        {/* engraved rank */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="font-mono-ar font-black text-6xl md:text-7xl text-black/25 select-none" style={{ textShadow: "0 1px 0 rgba(255,255,255,0.15)" }}>#{rank}</span>
                        </div>
                        {/* Top plate */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-b from-white/30 to-transparent"></div>
                      </motion.div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 text-[9px] tracking-[0.4em] text-zinc-500 font-mono-ar">OK FBI · MEDAL CEREMONY</div>
            </>
          )}
        </div>

        {/* Full leaderboard drawer */}
        <AnimatePresence>
          {showFull && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/85 backdrop-blur flex items-center justify-center p-6 z-[10]" onClick={() => setShowFull(false)}>
              <motion.div initial={{ scale: 0.96 }} animate={{ scale: 1 }} onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl">
                <LeaderboardPanel title="الترتيب الكامل للمحققين" />
                <button onClick={() => setShowFull(false)} data-testid="podium-close-full" className="btn-ghost mt-3 w-full">إغلاق</button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
